import { HistoryTransaction, InfluencerTransaction } from '@/hub-shared'
import Environment from '@interfaces/environment.interface'
import onEnvironmentChange from '@observables/environment.observable'
import LogService from '@services/log/log.service'
import { AxiosError, AxiosResponse } from 'axios'
import { take } from 'rxjs/operators'
import MainService from '../main/main.service'

export interface paymentHistory {
  options: {
    page: number
    pageSize: number
    type: string
  }
  squidId: string
  total: number
  transactions: Array<HistoryTransaction>
}

interface amountsReceived {
  statusFilter: {
    notIn: Array<string>
  }
  uniqueStatus: Array<string>
  campaigns: Array<string>
  totals: {
    totalCampaigns: number
    totalGrossValue: number
    totalNetValue: number
    totalTransactions: number
  }
}

export default class FinancialOperationService extends MainService {
  url: string
  logService = new LogService()

  constructor(financialOperationUrl?: string) {
    super()
    if (!financialOperationUrl) {
      onEnvironmentChange.pipe(take(1)).subscribe((environment: Environment) => {
        this.url = environment?.squidApis?.financialOperation
      })
    } else {
      this.url = financialOperationUrl
    }
    this.logService.init('Financial Operation Service')
  }

  public async postUnblockPayment(transactionId: string) {
    const headers = await this.getHeaders()
    return this.http
      .post(
        `${this.url}/transactions/${transactionId}/unblocked/hub`,
        {},
        {
          headers,
        }
      )
      .then((r: AxiosResponse) => r.data)
      .catch((error: AxiosError) => {
        this.logService.error({
          message: 'Error on unblock payment',
          error,
        })
        throw error.response
      })
  }

  public async getTransactionDetails(transactionId: string): Promise<InfluencerTransaction> {
    const headers = await this.getHeaders()
    return this.http
      .get(`${this.url}/transactions/${transactionId}/hub`, {
        headers,
      })
      .then((r: AxiosResponse) => r.data)
      .catch((error: AxiosError) => {
        this.logService.error({
          message: 'Error on get transaction details',
          error,
        })
        throw error.response
      })
  }

  public async getIncomeInfoUrl(): Promise<{ url: string }> {
    const headers = await this.getHeaders()
    const year = new Date().getFullYear()

    return this.http
      .get(`${this.url}/income-report/generate-link?year=${year - 1}`, { headers })
      .then((r: AxiosResponse) => {
        return r.data
      })
      .catch((error: any) => {
        this.logService.error({
          message: 'Error getting income info',
          error,
        })
        throw error.response
      })
  }

  public async getPaymentHistory(type: 'received' | 'toReceive' = null, page = 1, pageSize = 5): Promise<paymentHistory> {
    const headers = await this.getHeaders()
    return this.http
      .get(`${this.url}/transactions/hub?type=${type || 'all'}&page=${page}&pageSize=${pageSize}`, {
        headers,
      })
      .then((r: AxiosResponse) => r.data)
      .catch((error: AxiosError) => {
        this.logService.error({
          message: 'Error on get payment history',
          error,
        })
        throw error.response
      })
  }

  public async getAmountsReceived(notIn?: string | string[]): Promise<amountsReceived> {
    const returnNotIn = () => {
      if (notIn?.length > 1) {
        let returnValue = ''
        for (let aux = 0; aux < notIn?.length; aux++) {
          returnValue += `notIn=${notIn[aux]}${aux !== notIn?.length - 1 ? '&' : ''}`
        }
        return returnValue
      } else {
        return `notIn=${notIn}`
      }
    }

    const headers = await this.getHeaders()
    return this.http
      .get(`${this.url}/influencer-payments/balance/hub${notIn ? `?${returnNotIn()}` : ''}`, {
        headers,
      })
      .then((r: AxiosResponse) => r.data)
      .catch((error: AxiosError) => {
        this.logService.error({
          message: 'Error on get amounts received',
          error,
        })
        throw error.response
      })
  }
}
